import React, { useState, useEffect, useMemo } from "react";
import { Spin, Skeleton } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import DateSelector from "../../../commonComponents/DateSelector";
import {
  InfoTileCard,
  InfoTileHeader,
  InfoTileContent,
  InfoTileIconWrapper,
  InfoTileIcon,
  InfoTileTitle,
  InfoTileSubtitle,
  InfoTileValue,
} from "./styles";
import { InfoTileProps, DateRange } from "./constants";
import { useTheme } from "../../../context/ThemeContext";
import { addCommasInPrices } from "./constants";
import API from "../../../context/API";

const InfoTile: React.FC<InfoTileProps> = ({ title, subTitle, icon, merchant, storeId }) => {
  const { isDarkMode } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState<string | null>(null);
  const [itemData, setItemData] = useState<any[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: moment("2023-01-01"),
    endDate: moment(),
  });

  const value = useMemo(() => {
    return itemData.reduce(
      (sum, item) => sum + (parseFloat(String(item.totalRevenue)) || 0),
      0
    );
  }, [itemData]);

  const handleSelection = (selection: DateRange | { startDate: Date | null; endDate: Date | null }) => {
    let startDate: moment.Moment;
    let endDate: moment.Moment;
    
    if (moment.isMoment(selection.startDate)) {
      startDate = selection.startDate;
    } else if (selection.startDate) {
      startDate = moment(selection.startDate);
    } else {
      startDate = moment("2023-01-01");
    }
    
    if (moment.isMoment(selection.endDate)) {
      endDate = selection.endDate;
    } else if (selection.endDate) {
      endDate = moment(selection.endDate);
    } else {
      endDate = moment();
    }
    
    setSelectedDateRange({ startDate, endDate });
  };

  const fetchAnalyticsData = async () => {
    setIsLoading(true);
    setIsError(null);
    try {
      const params1 = {
        startDate: moment(selectedDateRange.startDate)?.format("YYYY-MM-DD"),
        endDate: moment(selectedDateRange.endDate)?.format("YYYY-MM-DD"),
        merchantId: merchant?.id,
        store: storeId,
      };
      const token = localStorage.getItem("access_token");
      const res = await API.get("/analytics/item-sales", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params1,
      });
      const itemsRes = res.data.data;
      const sortedItems = [...itemsRes.items].sort(
        (a, b) => b.totalTimesSold - a.totalTimesSold
      );

      setItemData(sortedItems);
    } catch (error) {
      console.error("Error fetching analytics data:", error);
      setIsError("Failed to load analytics data.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateRange, merchant, storeId]);

  const themeMode: "light" | "dark" = isDarkMode ? "dark" : "light";
  const styledTheme = { mode: themeMode } as DefaultTheme;

  return (
    <StyledThemeProvider theme={styledTheme}>
      <InfoTileCard>
        <Spin spinning={isLoading} tip="Loading...">
          <InfoTileHeader>
            <DateSelector onDateSelect={handleSelection} />
          </InfoTileHeader>

          <InfoTileContent>
            <InfoTileIconWrapper>
              {isLoading ? (
                <Skeleton.Avatar active size="large" />
              ) : (
                <InfoTileIcon>
                  <FontAwesomeIcon icon={icon} />
                </InfoTileIcon>
              )}
              <div>
                {isLoading ? (
                  <>
                    <Skeleton active paragraph={{ rows: 1 }} />
                    <Skeleton active paragraph={{ rows: 1, width: "60%" }} />
                  </>
                ) : (
                  <>
                    <InfoTileTitle level={4}>{title}</InfoTileTitle>
                    <InfoTileSubtitle>{subTitle}</InfoTileSubtitle>
                  </>
                )}
              </div>
            </InfoTileIconWrapper>
            {isLoading ? (
              <Skeleton active paragraph={{ rows: 1, width: 120 }} />
            ) : (
              <InfoTileValue level={2}>
                ${addCommasInPrices(value)}
              </InfoTileValue>
            )}
          </InfoTileContent>
          {isError && (
            <div style={{ marginTop: 16, color: "#ff4d4f" }}>
              {isError}
            </div>
          )}
        </Spin>
      </InfoTileCard>
    </StyledThemeProvider>
  );
};

export default InfoTile;
